import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react'
import { apiGet, getErrorMessage } from '../utils/api'

/**
 * Global WhatsApp connection status, polled every 20 seconds.
 *
 * Used by:
 *   - WhatsAppDisconnectedBanner (top of every page when state === 'disconnected')
 *   - Sidebar (red dot on Integrations link when disconnected)
 *   - IntegrationsPage (status card)
 *   - GroupsView / WhatsAppArchive (show "cached data" note + fall back to cached endpoint)
 *
 * The "disconnected" state fires if EITHER the OpenWA sidecar is unreachable
 * OR the WhatsApp session itself is not authenticated — see dev-server.ts
 * /api/integrations/whatsapp/status for the server-side state derivation.
 */

export type WhatsAppState = 'not_configured' | 'disconnected' | 'connected' | 'unknown'

export interface WhatsAppStatus {
  /** Derived, three-state summary the UI binds to. 'unknown' while the first fetch is in-flight. */
  state: WhatsAppState
  /** Raw server flags, useful for admin diagnostics */
  configured: boolean
  connected: boolean
  activeSession: { id?: string; status?: string } | null
  /** ISO timestamp of the last successful cache refresh, null if never synced. */
  last_synced_at: string | null
  cached_group_count: number
  /** Error string from the last failed poll, null while healthy. */
  error: string | null
  /** Force a refresh — used by the Reconnect button and after test/connect actions. */
  refresh: () => Promise<void>
}

const DEFAULT: WhatsAppStatus = {
  state: 'unknown',
  configured: false,
  connected: false,
  activeSession: null,
  last_synced_at: null,
  cached_group_count: 0,
  error: null,
  refresh: async () => {},
}

const POLL_INTERVAL_MS = 20_000

const WhatsAppStatusContext = createContext<WhatsAppStatus>(DEFAULT)

export const useWhatsAppStatus = () => useContext(WhatsAppStatusContext)

export function WhatsAppStatusProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<WhatsAppStatus>(DEFAULT)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchStatus = useCallback(async () => {
    try {
      const data = await apiGet<any>('/integrations/whatsapp/status')
      setStatus((prev) => ({
        ...prev,
        state: (data.state as WhatsAppState) || 'unknown',
        configured: !!data.configured,
        connected: !!data.connected,
        activeSession: data.activeSession || null,
        last_synced_at: data.last_synced_at || null,
        cached_group_count: data.cached_group_count || 0,
        error: null,
        refresh: fetchStatus,
      }))
    } catch (e) {
      // A failed poll itself is a strong signal — treat as disconnected.
      setStatus((prev) => ({
        ...prev,
        state: 'disconnected',
        error: getErrorMessage(e, 'Failed to check WhatsApp status'),
        refresh: fetchStatus,
      }))
    }
  }, [])

  useEffect(() => {
    fetchStatus()
    const loop = () => {
      timerRef.current = setTimeout(async () => {
        await fetchStatus()
        if (!document.hidden) loop()
        else {
          // Page is backgrounded — stop polling until it regains focus.
          const onVis = () => {
            if (!document.hidden) {
              document.removeEventListener('visibilitychange', onVis)
              fetchStatus()
              loop()
            }
          }
          document.addEventListener('visibilitychange', onVis)
        }
      }, POLL_INTERVAL_MS)
    }
    loop()
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [fetchStatus])

  return (
    <WhatsAppStatusContext.Provider value={{ ...status, refresh: fetchStatus }}>
      {children}
    </WhatsAppStatusContext.Provider>
  )
}
